import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Tooltip, Tag } from 'antd';
import {
  Card,
  DatePicker,
  Switch,
  Button,
  Space,
  Select,
  Row,
  Col,
  InputNumber,
  Divider,
  Collapse
} from 'antd';
import { ReloadOutlined, ClearOutlined, InfoCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get } from '../api/usersModule/httpClient';

const { useBreakpoint } = Grid;

const { RangePicker } = DatePicker;
const { Panel } = Collapse;

/**
 * Constants for date ranges
 */
const DATE_FORMAT = 'DD/MM/YYYY';
const DISPLAY_FORMAT = 'DD [de] MMM [de] YYYY';

/**
 * FiltersPanel - Componente reutilizable de panel de filtros para reportes
 * Características:
 * - Responsive design
 * - Validación de rangos de fechas
 * - Selección múltiple de proveedores con búsqueda
 * - Accesibilidad mejorada con tooltips
 *
 * @param {Object} props
 * @param {string} props.reportType - Tipo de reporte (para configuraciones futuras)
 * @param {Object} props.filters - Valores actuales de filtros
 * @param {Function} props.onFiltersChange - Callback cuando cambian los filtros
 * @param {Function} props.onApply - Callback al hacer clic en aplicar
 * @param {Function} props.onReset - Callback al hacer clic en limpiar
 * @param {boolean} props.loading - Indica si los datos están cargando
 * @param {string} props.project - NIT del proyecto para obtener proveedores
 */
function FiltersPanel({
  reportType,
  filters,
  onFiltersChange,
  onApply,
  onReset,
  loading = false,
  project = '1'
}) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;

  // State for providers
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [dateError, setDateError] = useState(null);
  const [providersExpanded, setProvidersExpanded] = useState(false);

  // State for article types and categories
  const [articleTypeOptions, setArticleTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingArticleTypes, setLoadingArticleTypes] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);

  /**
   * Load providers from API on mount
   */
  useEffect(() => {
    const loadProviders = async () => {
      setLoadingProviders(true);
      try {
        const response = await get(`/projects?Nit=${project}&list=${true}`);
        if (response && response.items) {
          setProviders(response.items);
        } else {
          setProviders([]);
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        setProviders([]);
      } finally {
        setLoadingProviders(false);
      }
    };

    loadProviders();
  }, [project]);

  /**
   * Load article types and categories from API on mount
   */
  useEffect(() => {
    const loadArticleTypes = async () => {
      setLoadingArticleTypes(true);
      try {
        const response = await get('/article-types?list=true');
        const items = response?.data || response?.items || (Array.isArray(response) ? response : []);
        setArticleTypeOptions(items.map(item => ({
          value: item.name,
          label: item.name
        })));
      } catch (error) {
        console.error('Error fetching article types:', error);
        setArticleTypeOptions([]);
      } finally {
        setLoadingArticleTypes(false);
      }
    };

    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await get('/categories?list=true');
        const items = response?.data || response?.items || (Array.isArray(response) ? response : []);
        setCategoryOptions(items.map(item => ({
          value: item.name,
          label: item.name
        })));
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategoryOptions([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadArticleTypes();
    loadCategories();
  }, []);

  /**
   * Build provider options with enhanced display
   */
  const providerOptions = providers.map((p) => ({
    value: p.Nit,
    label: `${p.ProjectName || p.NameProject} (${p.Nit})`,
    searchLabel: `${p.ProjectName || p.NameProject} ${p.Nit}`.toLowerCase()
  }));

  /**
   * Handle date range change with validation
   */
  const handleDateRangeChange = useCallback((dates) => {
    setDateError(null);

    // Validate date range
    if (dates && dates[0] && dates[1]) {
      if (dates[1].isBefore(dates[0])) {
        setDateError('La fecha final debe ser posterior a la fecha inicial');
        return;
      }

      // Check if range is too large (optional, adjust as needed)
      const daysDiff = dates[1].diff(dates[0], 'day');
      if (daysDiff > 365) {
        setDateError('El rango de fechas no puede exceder 1 año');
        return;
      }
    }

    const dateRange = {
      initialDate: dates?.[0] ? dates[0].format(DATE_FORMAT) : null,
      finalDate: dates?.[1] ? dates[1].format(DATE_FORMAT) : null
    };
    onFiltersChange('dateRange', dateRange);
  }, [onFiltersChange]);

  /**
   * Handle group by NIT toggle
   */
  const handleGroupByNitChange = useCallback((checked) => {
    onFiltersChange('groupByNit', checked);
    // Clear provider list when toggling off
    if (!checked) {
      onFiltersChange('nitList', []);
    }
  }, [onFiltersChange]);

  /**
   * Handle only active toggle
   */
  const handleOnlyActiveChange = useCallback((checked) => {
    onFiltersChange('onlyActive', checked);
  }, [onFiltersChange]);

  /**
   * Handle NIT list change (for multi-select)
   */
  const handleNitListChange = useCallback((values) => {
    onFiltersChange('nitList', values);
  }, [onFiltersChange]);

  /**
   * Handle articleType multi-select change
   */
  const handleArticleTypeChange = useCallback((values) => {
    onFiltersChange('articleType', values);
  }, [onFiltersChange]);

  /**
   * Handle category multi-select change
   */
  const handleCategoryChange = useCallback((values) => {
    onFiltersChange('category', values);
  }, [onFiltersChange]);

  /**
   * Get date picker value with proper parsing
   */
  const getDatePickerValue = () => {
    if (!filters.dateRange) return null;
    if (!filters.dateRange.initialDate || !filters.dateRange.finalDate) return null;

    // Parse DD/MM/YYYY format to dayjs
    const parseDate = (dateStr) => {
      const parts = dateStr.split('/');
      if (parts.length !== 3) return dayjs();
      const [day, month, year] = parts;
      return dayjs(`${year}-${month}-${day}`);
    };

    return [
      parseDate(filters.dateRange.initialDate),
      parseDate(filters.dateRange.finalDate)
    ];
  };

  /**
   * Check if filters have valid date range
   */
  const hasValidDateRange = () => {
    return filters.dateRange?.initialDate && filters.dateRange?.finalDate;
  };

  // Responsive label style
  const labelStyle = {
    marginBottom: isXs ? 6 : 8,
    fontWeight: 500,
    fontSize: isXs ? 13 : 14,
    color: '#374151'
  };

  // Helper for tooltip on switches
  const SwitchWithTooltip = ({ checked, onChange, children, tooltip }) => (
    <Tooltip title={tooltip} placement="top">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: isXs ? 13 : 14 }}>{children}</span>
        <Switch
          checked={checked}
          onChange={onChange}
          size="small"
        />
      </div>
    </Tooltip>
  );

  /**
   * Custom dropdown render for provider selection with checkboxes
   */
  const dropdownRender = (menu) => (
    <div style={{ padding: '8px 0' }}>
      {/* Select all / deselect all buttons */}
      <div style={{
        padding: '4px 12px 8px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 8,
        justifyContent: 'space-between'
      }}>
        <Button
          size="small"
          type="link"
          onClick={() => handleNitListChange(providerOptions.map(p => p.value))}
          style={{ padding: 0, fontSize: 12, height: 'auto' }}
        >
          Seleccionar todos
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => handleNitListChange([])}
          style={{ padding: 0, fontSize: 12, height: 'auto' }}
        >
          Limpiar
        </Button>
      </div>
      {/* Count indicator */}
      <div style={{
        padding: '4px 12px',
        fontSize: 11,
        color: '#8c8c8c',
        borderBottom: '1px solid #f0f0f0'
      }}>
        {filters.nitList?.length || 0} de {providerOptions.length} seleccionados
      </div>
      {menu}
    </div>
  );

  /**
   * Get provider name for display
   */
  const getProviderName = (nit) => {
    const provider = providers.find(p => p.Nit === nit);
    return provider?.ProjectName || provider?.NameProject || nit;
  };

  return (
    <Card
      size="small"
      className="shadow-sm"
      bodyStyle={{ padding: isXs ? 12 : 16 }}
      style={{
        borderRadius: '12px',
        border: dateError ? '2px solid #ff4d4f' : '1px solid #d9d9d9'
      }}
    >
      {/* Error message for date validation */}
      {dateError && (
        <div style={{
          marginBottom: 12,
          padding: '8px 12px',
          backgroundColor: '#fff2f0',
          border: '1px solid #ffccc7',
          borderRadius: '6px',
          fontSize: isXs ? 12 : 13,
          color: '#ff4d4f',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <InfoCircleOutlined />
          <span>{dateError}</span>
        </div>
      )}

      <Row gutter={[isXs ? 12 : 16, isXs ? 12 : 16]}>
        {/* Date Range */}
        <Col xs={24} sm={12} lg={6}>
          <div>
            <div style={labelStyle}>
              Rango de Fechas
              <span style={{ color: '#f5222d', marginLeft: 4 }}>*</span>
            </div>
            <RangePicker
              style={{ width: '100%' }}
              value={getDatePickerValue()}
              onChange={handleDateRangeChange}
              format={DATE_FORMAT}
              placeholder={['Fecha inicial', 'Fecha final']}
              allowClear
              size={isXs ? 'small' : 'middle'}
              status={dateError ? 'error' : undefined}
              disabledDate={(current) => {
                // Disable future dates
                return current && current.isAfter(dayjs(), 'day');
              }}
              showToday={false}
              inputReadOnly={false}
              popupStyle={{ zIndex: 2000 }}
            />
            {!hasValidDateRange() && (
              <div style={{
                marginTop: 4,
                fontSize: isXs ? 11 : 12,
                color: '#8c8c8c'
              }}>
                Selecciona un rango para ver datos
              </div>
            )}
          </div>
        </Col>

        {/* Grouping Options */}
        <Col xs={24} sm={12} lg={6}>
          <div>
            <div style={labelStyle}>
              Opciones de Agrupación
              <Tooltip title="Configura cómo se agruparán los datos en el reporte">
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12 }} />
              </Tooltip>
            </div>
            <Space direction="vertical" style={{ width: '100%' }} size={isXs ? 'small' : 'middle'}>
              <SwitchWithTooltip
                checked={filters.groupByNit || false}
                onChange={handleGroupByNitChange}
                tooltip="Agrupa los resultados por proveedor (NIT)"
              >
                Agrupar por Proveedor
              </SwitchWithTooltip>
              <SwitchWithTooltip
                checked={filters.onlyActive !== false}
                onChange={handleOnlyActiveChange}
                tooltip="Incluye únicamente SKUs con estado activo"
              >
                Solo SKUs Activos
              </SwitchWithTooltip>
            </Space>
          </div>
        </Col>

        {/* Provider Selection (conditional) */}
        {filters.groupByNit && (
          <Col xs={24} sm={12} lg={6}>
            <div>
              <div style={labelStyle}>
                Proveedores
                <Tooltip title="Selecciona uno o más proveedores para filtrar. Deja vacío para ver todos.">
                  <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12 }} />
                </Tooltip>
              </div>

              {/* Selector principal */}
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Seleccionar proveedores..."
                value={filters.nitList || []}
                onChange={handleNitListChange}
                allowClear={{
                  clearIcon: <Tooltip title="Limpiar selección">
                    <span>✕</span>
                  </Tooltip>
                }}
                showSearch
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip title={omittedValues.map(o => getProviderName(o)).join(', ')}>
                    <span>+{omittedValues.length} más</span>
                  </Tooltip>
                )}
                tagRender={(props) => {
                  const { label, value, closable, onClose } = props;
                  const onPreventMouseDown = (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  };
                  return (
                    <Tag
                      color="blue"
                      onMouseDown={onPreventMouseDown}
                      closable={closable}
                      onClose={onClose}
                      style={{
                        marginRight: 4,
                        marginBottom: 4,
                        fontSize: isXs ? 11 : 12,
                        maxWidth: isXs ? '80px' : '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                      }}
                    >
                      <Tooltip title={label}>
                        <span>{label}</span>
                      </Tooltip>
                    </Tag>
                  );
                }}
                size={isXs ? 'small' : 'middle'}
                loading={loadingProviders}
                filterOption={(input, option) =>
                  option?.searchLabel?.includes(input.toLowerCase()) ||
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={providerOptions}
                optionLabelProp="label"
                optionFilterProp="label"
                notFoundContent={loadingProviders ? "Cargando..." : "No hay proveedores"}
                virtual={false}
                listHeight={250}
                popupMatchSelectWidth
                dropdownStyle={{ minWidth: 320 }}
                dropdownRender={dropdownRender}
                showArrow
                suffixIcon={
                  <Tooltip title="Click para desplegar lista">
                    <DownOutlined />
                  </Tooltip>
                }
              />

              {/* Indicador de estado */}
              {filters.groupByNit && (!filters.nitList || filters.nitList.length === 0) && (
                <div style={{
                  marginTop: 4,
                  fontSize: isXs ? 11 : 12,
                  color: '#8c8c8c',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <span style={{ fontSize: 14 }}>🌐</span>
                  Se mostrarán todos los proveedores
                </div>
              )}

              {/* Resumen de proveedores seleccionados */}
              {filters.nitList && filters.nitList.length > 0 && (
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f0f7ff',
                  borderRadius: '6px',
                  border: '1px solid #bae0ff'
                }}>
                  <div
                    onClick={() => setProvidersExpanded(!providersExpanded)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: providersExpanded ? 8 : 0
                    }}
                  >
                    <span style={{
                      fontSize: isXs ? 11 : 12,
                      fontWeight: 500,
                      color: '#0050b3'
                    }}>
                      {filters.nitList.length} proveedor{filters.nitList.length > 1 ? 'es' : ''} seleccionado{filters.nitList.length > 1 ? 's' : ''}
                    </span>
                    {providersExpanded ?
                      <UpOutlined style={{ fontSize: 10, color: '#0050b3' }} /> :
                      <DownOutlined style={{ fontSize: 10, color: '#0050b3' }} />
                    }
                  </div>

                  {providersExpanded && (
                    <div style={{
                      maxHeight: '120px',
                      overflowY: 'auto',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px'
                    }}>
                      {filters.nitList.map(nit => (
                        <Tag
                          key={nit}
                          closable
                          onClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleNitListChange(filters.nitList.filter(n => n !== nit));
                          }}
                          style={{
                            fontSize: isXs ? 10 : 11,
                            marginBottom: 4,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {getProviderName(nit)} ({nit})
                        </Tag>
                      ))}
                    </div>
                  )}

                  {!providersExpanded && filters.nitList.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      marginTop: 4
                    }}>
                      {filters.nitList.slice(0, 2).map(nit => (
                        <Tag
                          key={nit}
                          style={{
                            fontSize: isXs ? 10 : 11,
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {getProviderName(nit)}
                        </Tag>
                      ))}
                      {filters.nitList.length > 2 && (
                        <Tag style={{ fontSize: isXs ? 10 : 11 }}>
                          +{filters.nitList.length - 2} más
                        </Tag>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Col>
        )}

        {/* Action Buttons */}
        <Col xs={24} sm={12} lg={filters.groupByNit ? 6 : 12}>
          <div>
            <div style={labelStyle}>Acciones</div>
            <Space wrap>
              <Button
                type="primary"
                onClick={onApply}
                loading={loading}
                icon={<ReloadOutlined />}
                size={isXs ? 'small' : 'middle'}
                block={isXs}
                disabled={!hasValidDateRange() || !!dateError}
              >
                {isSm ? 'Aplicar' : 'Filtrar'}
              </Button>
              <Button
                onClick={onReset}
                icon={<ClearOutlined />}
                size={isXs ? 'small' : 'middle'}
                block={isXs}
                disabled={!hasValidDateRange()}
              >
                Limpiar
              </Button>
            </Space>
            {hasValidDateRange() && (
              <div style={{
                marginTop: 8,
                fontSize: isXs ? 11 : 12,
                color: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <span style={{ fontSize: 14 }}>✓</span>
                Filtros listos para aplicar
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Second row: Article Type and Category filters */}
      <Row gutter={[isXs ? 12 : 16, isXs ? 12 : 16]} style={{ marginTop: isXs ? 12 : 16 }}>
        {/* Article Type selector */}
        <Col xs={24} sm={12} lg={8}>
          <div>
            <div style={labelStyle}>
              Tipo de Articulo
              <Tooltip title="Filtra por tipo de articulo (Materia Prima, Envase, etc.)">
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12 }} />
              </Tooltip>
            </div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Todos los tipos..."
              value={filters.articleType || []}
              onChange={handleArticleTypeChange}
              allowClear
              showSearch
              maxTagCount="responsive"
              size={isXs ? 'small' : 'middle'}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={articleTypeOptions}
              loading={loadingArticleTypes}
              notFoundContent={loadingArticleTypes ? "Cargando..." : "No hay opciones"}
              showArrow
              suffixIcon={<DownOutlined />}
            />
            {(!filters.articleType || filters.articleType.length === 0) && (
              <div style={{
                marginTop: 4,
                fontSize: isXs ? 11 : 12,
                color: '#8c8c8c'
              }}>
                Todos los tipos incluidos
              </div>
            )}
          </div>
        </Col>

        {/* Category selector */}
        <Col xs={24} sm={12} lg={8}>
          <div>
            <div style={labelStyle}>
              Categoria
              <Tooltip title="Filtra por categoria del SKU (Plastico, Vidrio, etc.)">
                <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12 }} />
              </Tooltip>
            </div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Todas las categorias..."
              value={filters.category || []}
              onChange={handleCategoryChange}
              allowClear
              showSearch
              maxTagCount="responsive"
              size={isXs ? 'small' : 'middle'}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={categoryOptions}
              loading={loadingCategories}
              notFoundContent={loadingCategories ? "Cargando..." : "No hay opciones"}
              showArrow
              suffixIcon={<DownOutlined />}
            />
            {(!filters.category || filters.category.length === 0) && (
              <div style={{
                marginTop: 4,
                fontSize: isXs ? 11 : 12,
                color: '#8c8c8c'
              }}>
                Todas las categorias incluidas
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Quick date shortcuts */}
      {!hasValidDateRange() && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Space wrap size={isXs ? 'small' : 'middle'}>
            <span style={{ fontSize: isXs ? 12 : 13, color: '#8c8c8c', fontWeight: 500 }}>
              Rápidos:
            </span>
            <Button
              size="small"
              type="link"
              onClick={() => {
                const today = dayjs();
                handleDateRangeChange([today.startOf('month'), today.endOf('month')]);
              }}
            >
              Este mes
            </Button>
            <Button
              size="small"
              type="link"
              onClick={() => {
                const today = dayjs();
                handleDateRangeChange([today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')]);
              }}
            >
              Mes anterior
            </Button>
            <Button
              size="small"
              type="link"
              onClick={() => {
                const today = dayjs();
                handleDateRangeChange([today.subtract(7, 'day'), today]);
              }}
            >
              Últimos 7 días
            </Button>
            <Button
              size="small"
              type="link"
              onClick={() => {
                const today = dayjs();
                handleDateRangeChange([today.subtract(30, 'day'), today]);
              }}
            >
              Últimos 30 días
            </Button>
          </Space>
        </>
      )}
    </Card>
  );
}

export default FiltersPanel;
